import { useEffect, useState } from 'react';

import { Button } from '#/components/Button/Button';
import { useAppConfig } from '#/config/AppConfigContext';
import { EmailSignupFormContainer } from '#/containers/EmailSignupFormContainer';
import { LoginFormContainer } from '#/containers/LoginFormContainer';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { AuthApiTmpStorage } from '#/modules/auth/api/tmpStorage/types/AuthApiTmpStorage';
import type { CmsApiSignUpBlockData } from '#/modules/cms/api/types/blocks/CmsApiSignUpBlock';
import { trackEvent } from '#/utils/analytics/track/trackEvent';

import {
  CmsSignUpBlock_Box_1,
  CmsSignUpBlock_Box_2,
  CmsSignUpBlockHolder,
} from './CmsSignUpBlock.styled';
import { CmsSignUpBlockHeadline } from './CmsSignUpBlockHeadline';

type Props = {
  data: CmsApiSignUpBlockData;
  isPageHeader: boolean;
};

export function CmsSignUpBlock({
  data: {
    headline,
    redirectUrl,
    bgImage,
    loggedInHeadline,
    loggedInRedirectButtonText,
  },
  isPageHeader,
}: Props) {
  const { appConfig } = useAppConfig();
  const { i18n } = useI18n();
  const tmpStorage = redirectUrl
    ? ({ type: 'redirect', redirectUrl } satisfies AuthApiTmpStorage)
    : undefined;

  const { user } = useUser();

  const [emailInUse, setEmailInUse] = useState('');

  useEffect(() => {
    trackEvent('View Sign Up Form');
  }, []);

  const showLoggedInUi = user && loggedInHeadline && !emailInUse;

  return (
    <div data-qa-id="sign-up">
      <CmsSignUpBlockHolder
        $imageHandle={bgImage?.handle}
        $mimetype={bgImage?.mimetype}
        $appConfig={appConfig}
      >
        <CmsSignUpBlock_Box_2>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <CmsSignUpBlock_Box_1>
              <CmsSignUpBlockHeadline
                text={showLoggedInUi ? loggedInHeadline : headline}
                isPageHeader={isPageHeader}
              />

              <div style={{ marginTop: '40px' }}>
                {showLoggedInUi && (
                  <>
                    {i18n(
                      'You are logged in as {{strong}}%(email)s{{/strong}}',
                      { email: user.email ?? '', strong: <strong /> },
                    )}

                    {redirectUrl && loggedInRedirectButtonText && (
                      <div style={{ marginTop: 24 }}>
                        <Button
                          type="link"
                          href={redirectUrl}
                          variant="primary"
                        >
                          {loggedInRedirectButtonText}
                        </Button>
                      </div>
                    )}
                  </>
                )}

                {!showLoggedInUi && (
                  <>
                    {!emailInUse && (
                      <EmailSignupFormContainer
                        analyticsTitle="[cms] Sign Up block - email available form"
                        usageContext="page"
                        tmpStorage={tmpStorage}
                        compactView
                        hideLoginBlock
                        customButtonText={i18n('Continue')}
                        setEmailInUse={setEmailInUse}
                      />
                    )}

                    {emailInUse && (
                      <LoginFormContainer
                        analyticsTitle="[cms] Sign Up block - email in use"
                        usageContext="page"
                        tmpStorage={tmpStorage}
                        email={emailInUse}
                        compactView
                      />
                    )}
                  </>
                )}
              </div>
            </CmsSignUpBlock_Box_1>
          </div>
        </CmsSignUpBlock_Box_2>
      </CmsSignUpBlockHolder>
    </div>
  );
}
